import { useCallback, useEffect, useRef } from 'react';

export interface ValidationEmbeddedActions {
  readonly openGuide: () => void;
  readonly openTour: () => void;
}

export type RegisterValidationEmbeddedActions = (
  actions: ValidationEmbeddedActions | null
) => void;

export type RegisterActiveValidationActions = (
  actions: ValidationEmbeddedActions
) => () => void;

export const useRegisterActiveRepairActions = (
  registerActiveActions: RegisterActiveValidationActions | undefined,
  openGuide: () => void,
  openRepairTour: (rememberOnboarding: boolean) => void
): void => {
  useEffect(() => {
    if (!registerActiveActions) {
      return;
    }

    return registerActiveActions({
      openGuide,
      openTour: () => openRepairTour(false),
    });
  }, [openGuide, openRepairTour, registerActiveActions]);
};

interface UseValidationEmbeddedActionsOptions {
  readonly embedded: boolean;
  readonly registerEmbeddedActions?: RegisterValidationEmbeddedActions;
  readonly openGuide: () => void;
  readonly openTour: () => void;
}

export const useValidationEmbeddedActions = ({
  embedded,
  registerEmbeddedActions,
  openGuide,
  openTour,
}: UseValidationEmbeddedActionsOptions): RegisterActiveValidationActions => {
  const consoleActionsRef = useRef<ValidationEmbeddedActions>({ openGuide, openTour });
  const activeActionsRef = useRef<ValidationEmbeddedActions | null>(null);
  consoleActionsRef.current = { openGuide, openTour };

  const registerActiveActions = useCallback<RegisterActiveValidationActions>((actions) => {
    activeActionsRef.current = actions;

    return () => {
      if (activeActionsRef.current === actions) {
        activeActionsRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!embedded || !registerEmbeddedActions) {
      return;
    }

    registerEmbeddedActions({
      openGuide: () => (activeActionsRef.current ?? consoleActionsRef.current).openGuide(),
      openTour: () => (activeActionsRef.current ?? consoleActionsRef.current).openTour(),
    });

    return () => registerEmbeddedActions(null);
  }, [embedded, registerEmbeddedActions]);

  return registerActiveActions;
};
